// Global entry point - loaded on every page
// Contains cross-page functionality like HTMX handlers, authentication checks, etc.


// Import subscription management components
import '../../css/budgetWarning.css';
import '../../css/upgradePrompt.css';
import { BudgetWarningManager } from '../utils/budgetWarning.js';
import { UpgradePromptManager } from '../utils/upgradePrompt.js';

// Initialize global questus namespace
window.questus = window.questus || {};

// Add hideLoadingOverlay function globally - hide ALL loading overlays
window.questus.hideLoadingOverlay = function() {
    // Hide all elements with loadingOverlay ID (there might be multiple due to templates)
    const overlays = document.querySelectorAll('#loadingOverlay, [id*="loading"], .loading-overlay');
    overlays.forEach(overlay => {
        overlay.style.display = 'none';
    });
    
    // Remove loading class from body
    document.body.classList.remove('loading');
    
    
    // Additional cleanup for any stuck overlays
    setTimeout(() => {
        const remainingOverlays = document.querySelectorAll('#loadingOverlay:not([style*="display: none"]), .loading-overlay:not([style*="display: none"])');
        if (remainingOverlays.length > 0) {
            console.warn(`⚠️ Found ${remainingOverlays.length} overlays still visible after hideLoadingOverlay, forcing hide`);
            remainingOverlays.forEach(overlay => {
                overlay.style.display = 'none';
                overlay.style.visibility = 'hidden';
                overlay.style.opacity = '0';
            });
        }
    }, 100);
};

// Import and expose URL helper functions globally
import { getUrlParam, getUrlParams, updateUrlParams } from '../utils/helpers/urlHelpers.js';

// Make URL helpers globally available
window.getUrlParam = getUrlParam;
window.getUrlParams = getUrlParams;
window.updateUrlParams = updateUrlParams;

// Import i18n
import { initializeUILanguage, translatePage } from '../utils/helpers/language.js';
// Import folder data loading functionality
import { loadAllData } from '../utils/app/load.js';
// Import folder UI initialization
import '../utils/ui/folderUI.js';
// Import UI initialization functions for jsTree
import { addBootstrapListeners } from '../utils/ui/initUI.js';
// Import global function exposer
import { exposeQuestusCore } from '../exposeGlobals.js';

// Manage info tabs visibility based on current page
function manageInfoTabsVisibility() {
    const infoTabsWrapper = document.getElementById('infoTabs');
    if (!infoTabsWrapper) return;

    const currentPath = window.location.pathname;
    const shouldShow = currentPath === '/' ||
                       currentPath === '/dashboard' ||
                       currentPath === '/welcome';

    infoTabsWrapper.style.display = shouldShow ? 'block' : 'none';
}


// Initialize global HTMX event handlers
function initGlobalHTMXHandlers() {
    if (typeof htmx === 'undefined') {
        console.warn('⚠️ HTMX not available, skipping global handlers');
        return;
    }

    // Clean up examples panel before swapping new content in
    document.body.addEventListener('htmx:beforeSwap', function(event) {
        if (event.detail.target && event.detail.target.id === 'content') {
            document.body.classList.remove('examples-panel-visible', 'examples-panel-collapsed');
            const infoSlot = document.getElementById('info-panel-slot');
            if (infoSlot) infoSlot.innerHTML = '';
        }
    });

    // Main HTMX afterSwap handler for layout management
    document.body.addEventListener('htmx:afterSwap', function(event) {
        const xhr = event.detail.xhr;
        if (!xhr) return;
        
        // Close mobile offcanvas menu if open
        const offcanvasElement = document.getElementById('offcanvasNavbar');
        if (offcanvasElement && typeof bootstrap !== 'undefined') {
            const offcanvasInstance = bootstrap.Offcanvas.getInstance(offcanvasElement);
            if (offcanvasInstance && offcanvasElement.classList.contains('show')) {
                offcanvasInstance.hide();
            }
        }
        
        // Get response headers
        const showFolderPaneHeader = xhr.getResponseHeader('HX-Show-Folder-Pane') || 'false';
        const fullWidthHeader = xhr.getResponseHeader('HX-Full-Width') || 'false';
        const isLoggedInHeader = xhr.getResponseHeader('HX-Is-Logged-In');
        
        // Handle folder panel visibility
        const folderPanel = document.getElementById('folder-panel');
        if (folderPanel) {
            // alert(showFolderPaneHeader === "true"); // Debug alert - uncomment if needed
            folderPanel.style.display = showFolderPaneHeader === 'true' ? 'flex' : 'none';

            // Update body classes for sidebar state
            if (typeof updateSidebarVisibility === 'function') {
                updateSidebarVisibility();
            }

            // Deselect jsTree items when navigating away from decks
            if (showFolderPaneHeader === 'true' && window.location.pathname !== '/deck/decks') {
                if (typeof $ !== 'undefined' && $('#sidebarFolderTree').jstree) {
                    $('#sidebarFolderTree').jstree('deselect_all');
                }
            }
        }
        
        // Handle content width
        const contentElement = document.getElementById('content');
        if (contentElement) {
            contentElement.classList.toggle('full-width', fullWidthHeader === 'true');
            contentElement.classList.toggle('container', fullWidthHeader !== 'true');
        }
        
        // Force reload if login state changed
        if (isLoggedInHeader === 'false') {
            if (document.querySelector('#userDropdown') || document.querySelector('#userDropdownMobile')) {
                window.location.reload();
            }
        }
        
        // Re-initialize Bootstrap tabs after content swap
        if (event.detail.target.id === 'content') {
            const tabTriggerList = document.querySelectorAll('[data-bs-toggle="tab"]');
            if (tabTriggerList.length > 0) {
                tabTriggerList.forEach(tabTriggerEl => {
                    if (typeof bootstrap !== 'undefined' && bootstrap.Tab) {
                        new bootstrap.Tab(tabTriggerEl);
                    }
                });
            }
        }
        
        // Manage info tabs visibility based on current page
        manageInfoTabsVisibility();
    });
    
    // HTMX afterRequest: update browser history when needed and hide loading overlays
    document.body.addEventListener('htmx:afterRequest', function(event) {
        // Always hide loading overlays after HTMX requests
        if (window.questus && window.questus.hideLoadingOverlay) {
            window.questus.hideLoadingOverlay();
        }
        
        if (!event.detail.xhr || !event.detail.elt) return;
        const responseUrl = event.detail.xhr.responseURL;
        const pushUrl = event.detail.elt.getAttribute('hx-push-url');
        if (pushUrl && pushUrl !== 'false') {
            history.pushState({}, '', pushUrl === 'true' ? responseUrl : pushUrl);
        }
    });
    
    // Clear stale state before HTMX swaps new content
    document.body.addEventListener('htmx:beforeSwap', function(event) {
        if (event.detail.target.id === 'content') {
            window.pageInitialization = null;
            // Clear examples panel slot when navigating away
            var slot = document.getElementById('info-panel-slot');
            if (slot) slot.innerHTML = '';
        }
    });

    // HTMX afterSettle: re-translate swapped content and call page initialization
    let initializationTimeout;
    document.body.addEventListener('htmx:afterSettle', function(event) {
        if (event.detail.target.id === 'content') {
            // Re-translate new content (i18next already has the correct language)
            translatePage();

            // Debounce multiple settle events
            clearTimeout(initializationTimeout);
            initializationTimeout = setTimeout(() => {
                // Final cleanup: ensure all loading overlays are hidden after page settle
                if (window.questus && window.questus.hideLoadingOverlay) {
                    window.questus.hideLoadingOverlay();
                }

                // Call page-specific initialization if it exists
                if (typeof window.pageInitialization === 'function') {
                    window.pageInitialization();
                }
            }, 50);
        }
    });
    
    // Handle browser back/forward navigation
    window.addEventListener('popstate', function() {
        htmx.ajax('GET', window.location.href, {
            target: '#content',
            swap: 'innerHTML'
        });
    });
    
}

// Initialize folder tree functionality
async function initFolderTree() {
    
    try {
        // Load all folder data (server, demo, local)
        await loadAllData();
        
        // Dispatch FolderDataReady event to trigger folder tree initialization
        document.dispatchEvent(new CustomEvent('FolderDataReady'));
        
        // Add jsTree listeners if jQuery is available
        if (typeof $ !== 'undefined' || typeof jQuery !== 'undefined') {
            addBootstrapListeners();
        } else {
            // Try again after a short delay if jQuery isn't loaded yet
            setTimeout(() => {
                if (typeof $ !== 'undefined' && !window.jsTreeGlobalInitialized) {
                    addBootstrapListeners();
                    window.jsTreeGlobalInitialized = true;
                }
            }, 500);
        }
        
    } catch (error) {
        console.error('❌ Failed to initialize folder tree:', error);
    }
}

// Wait for pageInitialization function to be defined and call it
async function waitForPageInitialization(maxWaitTime = 10000) {
    const startTime = Date.now();
    while (Date.now() - startTime < maxWaitTime) {
        if (typeof window.pageInitialization === 'function') {
            try {
                await window.pageInitialization();
            } catch (error) {
                console.error('❌ Error in pageInitialization:', error);
            }
            return;
        }
        // Check every 100ms
        await new Promise(resolve => setTimeout(resolve, 100));
    }
}

// Initialize subscription management systems
async function initSubscriptionSystems() {
    
    try {
        // Initialize budget warning manager
        const budgetWarningManager = new BudgetWarningManager();
        
        // Initialize upgrade prompt manager  
        const upgradePromptManager = new UpgradePromptManager();
        
        // Expose globally for compatibility with existing code
        window.budgetWarningManager = budgetWarningManager;
        window.upgradePromptManager = upgradePromptManager;
        
        // Only initialize if user is authenticated
        const isAuthenticated = window.appContext?.isLoggedIn || 
                              document.body.dataset.userAuthenticated === 'true' ||
                              document.querySelector('.user-menu, .user-dropdown');
        
        if (isAuthenticated) {
            await budgetWarningManager.init();
        } else {
        }
        
        // Upgrade prompts are handled by individual pages when needed
        
    } catch (error) {
        console.error('❌ Failed to initialize subscription systems:', error);
    }
}

// Initialize global functionality
async function initGlobal() {

    // Expose core questus functions globally
    exposeQuestusCore();

    // Cache subscription status (non-blocking)
    window.questus._isPro = false;
    fetch('/api/v1/subscription/status', { credentials: 'include' })
        .then(r => r.ok ? r.json() : null)
        .then(data => {
            if (data && data.authenticated && data.plan && data.plan !== 'free' && data.plan !== 'guest') {
                window.questus._isPro = true;
            }
        }).catch(() => {});

    // Initialize i18n (cookie-based, loads translations, applies to page)
    await initializeUILanguage();

    // Initialize HTMX handlers
    initGlobalHTMXHandlers();
    
    // Initialize folder tree for myDecksContent
    // Only initialize if we have the folder panel element
    const folderPanel = document.getElementById('folder-panel');
    if (folderPanel) {
        await initFolderTree();
        // Resizable sidebar
        const resizeHandle = document.getElementById('folder-panel-resize');
        if (resizeHandle) {
            let isResizing = false;
            resizeHandle.addEventListener('mousedown', (e) => {
                isResizing = true;
                resizeHandle.classList.add('active');
                document.body.style.cursor = 'col-resize';
                document.body.style.userSelect = 'none';
                e.preventDefault();
            });
            document.addEventListener('mousemove', (e) => {
                if (!isResizing) return;
                const newWidth = Math.min(480, Math.max(200, e.clientX));
                folderPanel.style.width = newWidth + 'px';
                document.querySelector('.main-container')?.style.setProperty('margin-left', newWidth + 'px');
                const header = document.querySelector('.stitch-header');
                if (header) header.style.width = `calc(100% - ${newWidth}px)`;
            });
            document.addEventListener('mouseup', () => {
                if (!isResizing) return;
                isResizing = false;
                resizeHandle.classList.remove('active');
                document.body.style.cursor = '';
                document.body.style.userSelect = '';
            });
        }
    } else {
    }
    
    // Wait for and call page-specific initialization
    // This allows each page to define its own pageInitialization function
    await waitForPageInitialization();
    
    // Hide loading overlay after everything is initialized
    if (window.questus && window.questus.hideLoadingOverlay) {
        window.questus.hideLoadingOverlay();
    }
    
    // Manage info tabs visibility on initial page load
    manageInfoTabsVisibility();
    
    // Initialize subscription management systems
    await initSubscriptionSystems();
    
    // Add any other global initialization here
    // For example:
    // - Authentication state monitoring
    // - Global keyboard shortcuts
    // - Analytics
    // - Error tracking
    
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initGlobal);
} else {
    initGlobal();
}

// Export for potential use by other modules
export { initGlobalHTMXHandlers };